import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CloseOutlined, IconPaperPlane, SearchOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Icon } from '../../components/ui/Icon'
import { Modal } from '../../components/ui/Modal'
import { useLoadMoreSentinel } from '../../lib/list/useLoadMoreSentinel'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import { toSafeImageRef } from '../../lib/thumbnail-image-ref'
import type { LibraryPartRow } from '../parts/types'
import { defaultPartsLibraryService, usePartsLibrary } from '../parts/usePartsLibrary'
import type { PartsLibraryService } from '../parts/usePartsLibrary'
import { PartThumbnail } from './PartThumbnail'

export interface NewRfqModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** Injectable for tests; defaults to the real BFF-backed service the
   * Parts Library screen already uses (`usePartsLibrary.ts`). See this
   * module's doc comment for why this modal reads through the SAME seam
   * rather than a new fetch (ADR-0094 D6). */
  readonly service?: PartsLibraryService
}

/** `[process||cat||'Custom', sessionName, 'Qty N'].filter(Boolean).join(' · ')`
 * (`.dc.html` `newRfqModal.parts[].meta`) — this repo has no `cat` field, so
 * the fallback chain is `suggestedProcess` (the closest FE equivalent) then
 * the literal `'Custom'`, matching the design's own ultimate fallback. */
function partMetaLine(row: LibraryPartRow): string {
  const process = row.sourceable.dfmSummary?.suggestedProcess ?? 'Custom'
  return [process, row.sessionName, `Qty ${row.quantity}`]
    .filter((value): value is string => Boolean(value))
    .join(' · ')
}

/** `[p.name, p.pn, p.process, p.cat].some(...)` (`.dc.html`'s `newRfqShown`
 * filter) — searched over name, part number (`catalogSku`), and the closest
 * FE equivalent of `process`/`cat`: DFM's `suggestedProcess` plus the row's
 * always-present free-text `requirements` summary. */
function partSearchHaystack(row: LibraryPartRow): string {
  return [
    row.sourceable.name,
    row.sourceable.catalogSku,
    row.sourceable.dfmSummary?.suggestedProcess,
    row.requirements,
  ]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ')
    .toLowerCase()
}

/**
 * The RFQ list's "+ New RFQ" entry point (UID-289, ADR-0094 D6 amendment;
 * design `Fictiv Platform.dc.html` L4379-4424 `newRfqModal`). A lightweight
 * pick-and-hand-off surface, NOT a replacement for the `/rfq/new` compose
 * page (`RfqReviewPage` stays reachable from Library/BOM/resume-draft/deep
 * links exactly as before) — its "Request quote" action reuses the SAME
 * queue-into-the-cross-screen-bundle-then-navigate pattern
 * `PartsLibraryPage.handleAddToRfq` already uses: `rfqBundle.beginDraft(...)`
 * then `navigate('/rfq/new')`, where the ADR-0082 eager-draft effect mints
 * the real `POST /rfq`. No new create path, no new endpoint.
 *
 * (Code-review W5 — this citation used to read `rfqBundle.addItems(...)`,
 * which was the pattern before UID-552/ADR-0170 retargeted every "Create
 * RFQ"/"Add to RFQ" producer entry point — this modal and
 * `PartsLibraryPage.handleAddToRfq` alike — onto `beginDraft`, so that each
 * one starts a genuinely NEW draft instead of merging into whichever slot
 * happened to be active. See `handleSubmit` below.)
 *
 * "Parts needing a quote" is the SAME `GET /parts?status=quote` read the
 * Parts Library screen's own "Quote" filter chip uses (`status: 'quote'`
 * <-> `LibraryStatus.quote_required`, see openapi.yaml's `LibraryRowStatus`
 * doc comment for the vocabulary alignment) — reused via `usePartsLibrary`,
 * not a second "needs-rfq" query or a new fetch. Search (name/MPN/process)
 * filters this ALREADY-fetched list client-side, matching the design's own
 * `newRfqShown` (a client filter over `newRfqCandidates`, not a re-query).
 *
 * Code-review C1 (UID-603) fix — `usePartsLibrary` paginates at
 * `PARTS_LIBRARY_PAGE_SIZE` (50) now, same as every other consumer. This
 * modal used to read `parts.length` as its "PARTS NEEDING A QUOTE (N)"
 * count and had no `loadMore` wiring at all, so an account with more than 50
 * parts needing a quote silently lost every part past the first page — both
 * uncounted AND unreachable. Fixed the same way `PartsLibraryPage.tsx` was:
 * the section-label count reads the server's `totalCount`, and the row list
 * — which lives inside `Modal`'s own scrollable `.modal-body` (`ui.css`) —
 * gets the SAME `useLoadMoreSentinel` infinite-scroll sentinel the page
 * uses, so scrolling the modal's list loads further pages instead of
 * stopping dead at 50.
 */
export function NewRfqModal({
  open,
  onClose,
  service = defaultPartsLibraryService,
}: NewRfqModalProps) {
  const navigate = useNavigate()
  const rfqBundle = useRfqBundle()
  const [search, setSearch] = useState('')
  // UID-610 — `useRangeSelection` additionally carries the Gmail/Finder
  // shift-click anchor.
  const rangeSelection = useRangeSelection()
  const { selectedIds } = rangeSelection

  // Fixed query — `status: 'quote'` only. Never re-issued for `search`
  // below (that's a client-side filter over this one fetch's — now
  // possibly multi-page — result; see the caveat on `noMatches` below).
  const { state, loadMore, refetch, isLoadingMore, loadMoreError, loadMoreErrorIsCursorRejection } =
    usePartsLibrary({ status: 'quote' }, service)
  const parts = state.status === 'ready' ? state.listing.parts : []
  // Code-review C1 — reads the server's `totalCount` (the true count for
  // `status: 'quote'`, independent of how many pages have loaded so far),
  // not `parts.length` (which is just "how many rows have loaded"). Falls
  // back to `parts.length` only when the response carries no `totalCount`
  // at all (the "this response was not paginated" case, `types.ts`'s
  // `LibraryListing` doc comment) — matching `PartsLibraryPage.tsx`'s same
  // fallback.
  const totalCount = state.status === 'ready' ? (state.listing.totalCount ?? parts.length) : 0
  const hasNextPage = state.status === 'ready' && state.listing.nextCursor != null
  // Code-review W2/W4 (UID-603 second pass) — `rootSelector` measures the
  // 600px prefetch lead against `Modal`'s own `.modal-body` scrolling
  // ancestor rather than the viewport (this modal's list scrolls inside
  // that div, not the page); see `useLoadMoreSentinel`'s doc comment.
  // `recheckKey` forces the observer to re-check the sentinel's current
  // intersection state every time a page is appended — covers the case
  // where the newly-appended page is ALSO filtered away by `search` below,
  // leaving the sentinel still intersecting without ever crossing a fresh
  // threshold, which would otherwise stall pagination one page in (see the
  // hook's `recheckKey` doc comment for the full mechanism).
  //
  // H4 (code-review UID-603 fourth pass) — `parts.length` (a loaded-item
  // COUNT) used to be the key here. That's lossy: if a page's rows fully
  // dedupe against what's already loaded (`useCursorList.ts`'s
  // `dedupeAppend`), the loaded count doesn't change even though a real
  // fetch completed and there may still be more to load, so the observer
  // never re-checks. `state.listing.nextCursor` changes on every successful
  // `loadMore()` (it advances, or reaches the end) independent of whether
  // the page's rows deduped away, so it's used as the recheck key instead —
  // same fix as `RfqListPage.tsx`'s `RfqServerTabPanel`.
  const loadMoreSentinelRef = useLoadMoreSentinel(loadMore, hasNextPage, {
    rootSelector: '.modal-body',
    recheckKey: state.status === 'ready' ? state.listing.nextCursor : null,
  })

  const query = search.trim().toLowerCase()
  // NOTE (same caveat as `PartsLibraryPage.tsx`'s W5): this search filters
  // only the rows loaded SO FAR — a match on a not-yet-fetched page won't
  // show up until the user scrolls (triggering `loadMore` above) far enough
  // to load it. `status: 'quote'` has no server-side `q` param today, so a
  // true server-side search isn't available here yet.
  const shownParts = useMemo(
    () => (query ? parts.filter((row) => partSearchHaystack(row).includes(query)) : parts),
    [parts, query],
  )

  const isEmpty = totalCount === 0
  const noMatches = totalCount > 0 && shownParts.length === 0
  const selectedCount = selectedIds.size

  /** UID-610 — shift-click range selection over the currently rendered
   * (search-filtered) part order (Gmail/Finder semantics, same as UID-470's
   * Parts Library). `shiftKey` reaches here from the row checkbox's
   * `onClick`, not `onChange` (a `ChangeEvent` carries no modifier-key
   * state). */
  function toggle(id: string, shiftKey: boolean) {
    rangeSelection.toggle(
      id,
      shownParts.map((row) => row.sourceable.id),
      shiftKey,
    )
  }

  // Memoized (empty deps — `setSearch`/`rangeSelection.clear` both close
  // over React-guaranteed-stable setState setters) so `handleClose` below
  // can also be memoized.
  // biome-ignore lint/correctness/useExhaustiveDependencies: rangeSelection.clear closes over the same stable setState every render, so omitting it doesn't risk a stale write.
  const reset = useCallback(() => {
    setSearch('')
    rangeSelection.clear()
  }, [])

  // `useCallback`-memoized (unlike this module's other handlers) because
  // this is the one passed straight through as `Modal`'s `onClose` prop —
  // `Modal.tsx`'s focus-management effect depends on `onClose` by
  // reference and RE-RUNS (re-focusing the dialog container) whenever that
  // reference changes. A fresh inline closure here would re-run that effect
  // on every keystroke in the search box below, stealing focus off the
  // input after the first character.
  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  function handleUpload() {
    reset()
    onClose()
    navigate('/uploads')
  }

  function handleSubmit() {
    if (selectedCount === 0) return
    const selected = parts.filter((row) => selectedIds.has(row.sourceable.id))
    // UID-335 follow-up — `thumbnailUrl` is a SIBLING field of `sourceable`
    // (never on `sourceable.imageRef` itself). See `toSafeImageRef`'s doc
    // comment for why the merge needs the root-relative-vs-absolute
    // distinction it applies.
    // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: this modal's
    // "Request quote" action is a "Create RFQ"/"Add to RFQ" entry point and
    // must start a genuinely NEW draft, never merge into whatever slot
    // happened to be active.
    rfqBundle.beginDraft(
      selected.map((row) => {
        const imageRef = toSafeImageRef(row.thumbnailUrl, row.sourceable.id)
        return {
          sourceable: imageRef !== undefined ? { ...row.sourceable, imageRef } : row.sourceable,
          name: row.sourceable.name ?? 'Part',
          sku: row.sourceable.catalogSku ?? null,
        }
      }),
    )
    reset()
    onClose()
    navigate('/rfq/new')
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New RFQ request"
      maxWidth={580}
      className="new-rfq-modal"
      headerContent={
        <div className="new-rfq-modal-header">
          <div className="new-rfq-modal-header-top">
            <span className="new-rfq-modal-icon-chip" aria-hidden="true">
              <Icon name="document" />
            </span>
            <h2 className="new-rfq-modal-title">New RFQ request</h2>
          </div>
          <p className="new-rfq-modal-subtitle">
            Select the parts in your library that need a quote, or upload new files.
          </p>
        </div>
      }
      footer={
        <>
          <Button variant="outline" className="new-rfq-modal-upload-btn" onClick={handleUpload}>
            <Icon name="upload" size="sm" />
            Upload new parts
          </Button>
          <span className="new-rfq-modal-footer-spacer" />
          <Button
            variant="primary"
            className="new-rfq-modal-submit-btn"
            disabled={selectedCount === 0}
            onClick={handleSubmit}
          >
            <IconPaperPlane />
            {selectedCount > 0 ? `Request quote (${selectedCount})` : 'Request quote'}
          </Button>
        </>
      }
    >
      <p className="new-rfq-modal-section-label">PARTS NEEDING A QUOTE ({totalCount})</p>

      {isEmpty && (
        <p className="new-rfq-modal-empty">
          No parts in your library need a quote right now. Upload new files to start an RFQ.
        </p>
      )}

      {!isEmpty && (
        <div className="new-rfq-modal-search-wrap">
          <span className="new-rfq-modal-search-icon" aria-hidden="true">
            <SearchOutlined />
          </span>
          <label className="sr-only" htmlFor="new-rfq-modal-search">
            Search parts by name, MPN, or process
          </label>
          <input
            id="new-rfq-modal-search"
            type="search"
            className="new-rfq-modal-search-input"
            placeholder="Search parts by name, MPN, or process…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search.length > 0 && (
            <button
              type="button"
              className="new-rfq-modal-search-clear"
              aria-label="Clear search"
              onClick={() => setSearch('')}
            >
              <CloseOutlined aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {noMatches && <p className="new-rfq-modal-empty">No parts match your search.</p>}

      {shownParts.length > 0 && (
        <ul className="new-rfq-modal-list">
          {shownParts.map((row) => {
            const id = row.sourceable.id
            const name = row.sourceable.name ?? 'Part'
            const checked = selectedIds.has(id)
            /* UID-457 item 2 — `?? '—'` alone treated an EMPTY-STRING
             * `catalogSku` as a real MPN and badged a blank chip, so the
             * fallback is keyed on a trimmed truthiness check, not just
             * nullishness. When there is no MPN the chip chrome comes off
             * (`-empty`) and the em-dash renders as plain muted text. */
            const sku = row.sourceable.catalogSku?.trim() || null
            return (
              <li key={id}>
                <label
                  className={
                    checked ? 'new-rfq-modal-row new-rfq-modal-row-selected' : 'new-rfq-modal-row'
                  }
                >
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={checked}
                    // UID-610 — reads `event.shiftKey` off `onClick`;
                    // `onChange` carries no modifier-key state, so it
                    // stays a no-op (see `toggle`'s doc comment).
                    onClick={(event) => toggle(id, event.shiftKey)}
                    onChange={() => {}}
                    aria-label={`Select ${name}`}
                  />
                  <PartThumbnail className="new-rfq-modal-row-thumb" url={row.thumbnailUrl} />
                  <span className="new-rfq-modal-row-text">
                    <span className="new-rfq-modal-row-name" title={name}>
                      {name}
                    </span>
                    <span className="new-rfq-modal-row-meta">{partMetaLine(row)}</span>
                  </span>
                  <span
                    className={
                      sku
                        ? 'new-rfq-modal-row-pn'
                        : 'new-rfq-modal-row-pn new-rfq-modal-row-pn-empty'
                    }
                  >
                    {sku ?? '—'}
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      )}

      {/* Code-review C1 (UID-603) — infinite-scroll sentinel, same pattern as
        `PartsLibraryPage.tsx`'s: ~600px lead so the next page is already
        loading before the shopper scrolls the modal's list to the true
        bottom. `Modal`'s own `.modal-body` is this modal's scroll
        container (`ui.css`) and the rows above land inside it as `Modal`'s
        children, so a sentinel placed here (a sibling of the list) scrolls
        with it — no extra markup needed to make the observer meaningful.
        Renders no markup/observer once there's no next page. */}
      {hasNextPage && (
        <div ref={loadMoreSentinelRef} className="new-rfq-modal-load-more-sentinel">
          <output aria-live="polite" className="new-rfq-modal-load-more-status">
            {isLoadingMore ? 'Loading more parts…' : ''}
          </output>
          {loadMoreError && (
            <div role="alert" className="new-rfq-modal-load-more-error">
              <p>{loadMoreError}</p>
              {/* A cursor-rejection error means the server can never resolve
                  this cursor — retrying via `loadMore` would resend the
                  SAME dead cursor forever. `refetch` resets to page 1
                  instead; see `PartsLibraryPage.tsx`'s same pattern. */}
              <button
                type="button"
                className="btn-link"
                onClick={loadMoreErrorIsCursorRejection ? refetch : loadMore}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
